import type { AiChatSidebarProps, AttachmentKey } from "./AiChatSidebar";

// "Context follows the view" (the new Studio): everything on screen is
// sent unless the user hides it. These are the pure rules; the sidebar's
// state hook applies them and the bar renders them.

/** The engine truncates a context item past this many lines
 *  (mstream MAX_CONTEXT_LINES); the bar says so on the chip. */
export const CONTEXT_MAX_LINES = 200;

export function isTruncated(text: string | undefined): boolean {
  if (!text) return false;
  // Like the engine's line count: a trailing newline isn't a line.
  return text.replace(/\n$/, "").split("\n").length > CONTEXT_MAX_LINES;
}

/** Whether the pipeline chip has something behind it: code that parses
 *  (the structured item), or code that does not but is there to send as
 *  text. An empty editor is neither. */
export function pipelinePresent(
  view: AiChatSidebarProps["pipelineView"],
): boolean {
  if (!view) return false;
  return !view.parseError || !!view.code?.trim();
}

// What each chip needs to have something behind it.
const PRESENT: Array<[AttachmentKey, (p: AiChatSidebarProps) => boolean]> = [
  ["input", (p) => !!p.inputDocument?.trim()],
  ["schema", (p) => !!p.schema?.trim()],
  ["tests", (p) => (p.testCases ?? []).length > 0],
  ["pipeline", (p) => pipelinePresent(p.pipelineView)],
];

/** The attachments that have something behind them right now. */
export function availableAttachments(
  props: AiChatSidebarProps,
): Set<AttachmentKey> {
  const keys = new Set<AttachmentKey>(
    PRESENT.filter(([, present]) => present(props)).map(([key]) => key),
  );
  for (const s of props.scripts ?? []) {
    if (s.content.trim()) keys.add(`script:${s.name}`);
  }
  return keys;
}

/** Everything available minus what the user hid. */
export function followsViewAttachments(
  props: AiChatSidebarProps,
  excluded: ReadonlySet<AttachmentKey>,
): Set<AttachmentKey> {
  const keys = availableAttachments(props);
  for (const key of excluded) keys.delete(key);
  return keys;
}
